/**
 * TransformController — on-canvas rotate + resize handles for leather objects
 * (#971). The leather analogue of construction's RotationController +
 * ResizeController, but lean: none of construction's mirror / group /
 * snap-chain / primitive entanglement applies, so rather than drive those
 * ~2800-line controllers this owns just the three leather drag states.
 *
 * What it does NOT own is the geometry (#990): where the handles sit, what the
 * cursor has to hit to grab them, the un-rotation into the object's own frame,
 * the default pivot and one frame of resize all come from
 * `model/transformHandles`, which construction reads too — so a handle can't be
 * drawn in one place and grabbed in another, and a fix lands once. The box
 * math (`constructionResize`), the path transforms (`rotatePathData`) and the
 * overlay renderer (`buildTransformOverlay`) were already shared.
 *
 * A tool OWNS a controller and DRIVES it by composition (mirrors
 * `OutlineEditController`): everything object-specific is reached through the
 * injected `TransformTarget` (bounds, pivot, live preview item, commit) and
 * `TransformHost` (scope / scene / settings). `OutlineEditTool` delegates its
 * `onDown/onMove/onUp/onHover/onCancel` here when the Edit action is
 * "transform".
 */
import type paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene } from "../canvas/Scene";
import type { ToolSettings } from "./EditorContext";
import type { Piece, Vec2 } from "../model/Project";
import type { Box, ResizeHandle } from "../model/constructionResize";
import {
  applyResizeStep,
  displayedBoxCenter,
  pickTransformHandle,
  resizeCursorFor,
  resizeDragPivot,
  unrotatePoint,
} from "../model/transformHandles";
import { isCenterSnapActive, isSizesSnapActive } from "./dragSnap";
import { rotationSweepRays } from "./rotationSweep";
import {
  currentPointerKind,
  hitRadiusFor,
  snapRadiusFor,
  type PointerKind,
} from "../canvas/pointerPrecision";

/** A transformable leather object (piece / cutout / stitch seam). The
 *  controller only ever talks to the object through this interface, so all
 *  three kinds plug into one drag pipeline. */
export interface TransformTarget {
  /** Stable identity ("piece:<id>" / "cutout:<pid>:<cid>" / "stitch:<pid>:<sid>").
   *  A key change mid-drag aborts (the object was swapped or deleted). */
  key: string;
  /** Human noun for undo labels: "part" / "cutout" / "seam". */
  noun: string;
  /** World-mm axis-aligned bounding box in the object's UN-ROTATED frame (the
   *  frame the handles + resize math operate in), or null when unmeasurable. */
  bounds(): Box | null;
  /** The object's persisted baked-rotation frame, or null when un-tilted. The
   *  controller un-rotates the cursor by this before all box-space math so a
   *  tilted object's handles hit + resize without skew (#971). */
  rotation(): { deg: number; center: { x: number; y: number } } | null;
  /**
   * Whether single-dimension EDGE handles are offered at all.
   *
   * True for every leather target since #1055. It stays on the interface
   * because construction's own resize path has kinds that genuinely have no
   * edges to offer (a Line, a pill), and both paths share this controller.
   *
   * Seams used to answer false, which is the wrong SHAPE of rule: it is a
   * property of an AXIS, not of an object kind. A curved seam has extent both
   * ways and wants all four handles; a perfectly flat cutout wants neither
   * pair. `offeredEdges` decides that per axis, for every kind alike.
   */
  allowsEdgeResize: boolean;
  /**
   * Force an aspect-locked (uniform) resize regardless of modifiers.
   *
   * False everywhere on leather since #1055. Seams forced it on the stated
   * grounds that "a slanted-hole seam can't be scaled non-uniformly", which
   * does not survive contact with the code: `resizeStitchLineByBox` scales the
   * geometry and leaves `pitch` and `size` alone, and the holes are DERIVED,
   * not stored — so a stretched seam re-lays them at the maker's unchanged
   * pitch along the new path, and a slant angle measured from the local seam
   * direction follows the new geometry.
   */
  aspectLockOnly: boolean;
  /** Floor for width / height (mm). */
  minSize: number;
  /** The owning PART id — the piece whose full geometry the Scene re-renders
   *  live during a drag (so holes / stitch / cutouts / decorations track). */
  pieceId: string;
  /** Produce the fully-transformed owning PART for a live rotate preview,
   *  reusing the same model fn as `commitRotate` so preview == result. Null when
   *  unmeasurable. */
  previewRotate(deg: number, cx: number, cy: number): Piece | null;
  /** Produce the fully-transformed owning PART for a live resize preview,
   *  reusing the same model fn as `commitResize`. Null when unmeasurable. */
  previewResize(
    orig: Box,
    next: Box,
    flip: { x: boolean; y: boolean },
  ): Piece | null;
  /** Commit a rotation by `deg` about (`cx`,`cy`) under an undo label. */
  commitRotate(label: string, deg: number, cx: number, cy: number): void;
  /** Commit a resize mapping `orig`→`next` (with axis `flip`s). */
  commitResize(
    label: string,
    orig: Box,
    next: Box,
    flip: { x: boolean; y: boolean },
  ): void;
  /** The pivot the user parked on THIS object, or null when it still sits at
   *  the default. Read from the model, not from view state, so it survives
   *  selecting something else, switching tools, and a reload — the same reason
   *  construction keeps `ConstructionLine.pivotPoint` on its model (#420). */
  pivot(): Vec2 | null;
  /** Persist a parked pivot (or clear it with null). */
  commitPivot(label: string, p: Vec2 | null): void;
  /** How far the object is drawn from its model geometry. A cutout or seam on
   *  a linked part moved in the Spread view is drawn at the part's cut-layout
   *  offset (#1140). Everything above is in MODEL space, so the controller maps
   *  the cursor in by this. Absent means zero. */
  displayOffset?(): Vec2;
}

export interface TransformHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getSettings(): ToolSettings;
  getTransformTarget(): TransformTarget | null;
  /** Re-pick the hover cursor after a drag completes. */
  refreshEditCursor(p: { x: number; y: number }): void;
  /** Snap a dragged resize handle (in DISPLAYED space) against every enabled
   *  snap, leaving out the object `targetKey` names, and show the in-draw snap
   *  indicator (#1174). Returns the cursor unchanged when nothing fires. */
  snapResizeHandle(cursor: Vec2, targetKey: string): Vec2;
  /** Drop the indicator and any per-drag snap state. */
  clearResizeHandleSnap(): void;
}

/** Grab radius (screen px at zoom 1) for the knob / pivot / resize squares —
 *  matches the construction handles' screen-constant sizing (#864). Where the
 *  handles SIT is shared (`transformHandles`); how generously they grab stays
 *  per-side tuning (#990). A finger grabs wider — see `pointerPrecision`; this
 *  is the fine-pointer figure. */
const HANDLE_HIT_RADIUS_MM = 3.2;
/** The rotation knob grabs twice as wide as the squares beside it, on every
 *  platform. It is a single control with empty space around it — it sits off
 *  the top of the box, where nothing else competes — while the resize squares
 *  sit in a ring of eight and only steal from each other when widened. So the
 *  one handle that CAN afford a generous zone gets one. */
const KNOB_HIT_RADIUS_MM = HANDLE_HIT_RADIUS_MM * 2;
/** Dropping the pivot crosshair back within this distance (mm) of the object's
 *  default position clears the parked pivot rather than storing a point that is
 *  the default in all but name — the only way to un-park one (#420 parity). */
const PIVOT_SNAP_BACK_MM = 1;
/** Below this drag distance (mm) a rotate/resize press is treated as a click
 *  (no-op), so a stray tap doesn't nudge the object. */
const DRAG_SLOP_MM = 0.2;

export class TransformController {
  private rotateDrag: {
    key: string;
    /** Pivot the rotation orbits. */
    pivot: { x: number; y: number };
    /** Cursor→pivot angle at press (deg), the zero of the sweep. */
    startAngle: number;
    lastDeg: number;
    moved: boolean;
  } | null = null;

  private resizeDrag: {
    key: string;
    handle: ResizeHandle;
    orig: Box;
    /** The rotation frame at press. `orig` and every `next` box are measured
     *  in it, so the cursor mapping and the live pivot use it too — reading
     *  it live would pick up the resized PREVIEW's rotation centre (#1111). */
    rot: { deg: number; center: Vec2 } | null;
    lastFlip: { x: boolean; y: boolean };
    lastNext: Box;
    moved: boolean;
  } | null = null;

  private pivotDrag: {
    key: string;
    moved: boolean;
    /** Whether the object already had a parked pivot when the drag started —
     *  so a nudge that lands back on the default can tell "cleared it" from
     *  "changed nothing". */
    hadPivot: boolean;
  } | null = null;

  constructor(private host: TransformHost) {}

  /**
   * The pivot the user parked on the current target, or null when it still
   * sits at the default.
   *
   * Two layers, and the split is the point. The MODEL (`target.pivot()`) is
   * where a parked pivot lives, so it survives selecting something else,
   * switching tools, undo/redo and a reload — the same contract construction
   * has had via `ConstructionLine.pivotPoint` since #420. The Scene override on
   * top of it is purely the LIVE drag: it lets the crosshair follow the cursor
   * before anything is committed, so a cancelled drag leaves the stored pivot
   * untouched.
   */
  private pivotOverride(): { x: number; y: number } | null {
    const target = this.host.getTransformTarget();
    if (!target) return null;
    const live = this.host.getScene().getTransformPivotOverride(target.key);
    return live ?? target.pivot();
  }

  hasActiveDrag(): boolean {
    return !!(this.rotateDrag || this.resizeDrag || this.pivotDrag);
  }

  reset(): void {
    if (this.resizeDrag) this.host.clearResizeHandleSnap();
    this.rotateDrag = null;
    this.resizeDrag = null;
    this.pivotDrag = null;
    this.host.getScene().setTransformPivotOverride(null);
    this.host.getScene().setTransformPreview(null);
    this.host.getScene().setGuideRays(null);
  }

  /** Map a cursor from where the object is DRAWN into its model space — the
   *  space every box, pivot and transform here is in (#1140). Applied once at
   *  each public entry point; a no-op unless the object is drawn shifted. */
  private toModel(p: Vec2): Vec2 {
    const off = this.host.getTransformTarget()?.displayOffset?.();
    return off && (off.x !== 0 || off.y !== 0)
      ? { x: p.x - off.x, y: p.y - off.y }
      : p;
  }

  private targetRotation(): { deg: number; center: Vec2 } | null {
    return this.host.getTransformTarget()?.rotation() ?? null;
  }

  /** Map a WORLD point into the target's UN-ROTATED frame (undo the baked
   *  tilt), so all box-space hit-testing + resize math is skew-free (#971). */
  private toLocal(p: Vec2, rot: { deg: number; center: Vec2 } | null): Vec2 {
    if (!rot) return p;
    return unrotatePoint(p, rot.center, rot.deg);
  }

  /** The effective pivot (a WORLD point) a rotation orbits: the dragged
   *  override, else the object's displayed centre (`displayedBoxCenter`, shared
   *  with construction's `#420` pivot rule and the Scene's crosshair). */
  private effectivePivot(
    box: Box,
    rot: { deg: number; center: Vec2 } | null,
  ): Vec2 {
    return this.pivotOverride() ?? displayedBoxCenter(box, rot);
  }

  private zoom(): number {
    const z = this.host.getScope().view?.zoom;
    return z && z > 0 ? z : 1;
  }

  private hitRadius(pointer?: PointerKind): number {
    return hitRadiusFor(HANDLE_HIT_RADIUS_MM, pointer) / this.zoom();
  }

  /** Which transform handle (if any) the cursor is over, for the given
   *  un-rotated `box`. The cursor is mapped into the object's un-rotated frame
   *  first, so a tilted object's rotated handles still hit correctly (#971). */
  handleAt(
    p: { x: number; y: number },
    box: Box,
    pointer?: PointerKind,
  ): "knob" | "pivot" | ResizeHandle | null {
    return this.handleAtModel(this.toModel(p), box, pointer);
  }

  private handleAtModel(
    p: { x: number; y: number },
    box: Box,
    pointer?: PointerKind,
  ): "knob" | "pivot" | ResizeHandle | null {
    const rot = this.targetRotation();
    const lp = this.toLocal(p, rot);
    const r = this.hitRadius(pointer);
    // The pivot crosshair is drawn axis-aligned at a WORLD point (it doesn't
    // tilt with the object), so it alone hit-tests against the raw cursor `p`
    // and stays this controller's business; the box handles live in the
    // un-rotated frame and are picked by the shared geometry (#990).
    const nearWorld = (x: number, y: number) =>
      (p.x - x) ** 2 + (p.y - y) ** 2 <= r * r;

    const pivot = this.effectivePivot(box, rot);
    if (this.pivotOverride() && nearWorld(pivot.x, pivot.y)) return "pivot";

    const handle = pickTransformHandle(box, lp, {
      zoom: this.host.getScope().view?.zoom ?? 1,
      knobRadiusMm: hitRadiusFor(KNOB_HIT_RADIUS_MM, pointer),
      resizeRadiusMm: hitRadiusFor(HANDLE_HIT_RADIUS_MM, pointer),
      allowEdgeResize: !!this.host.getTransformTarget()?.allowsEdgeResize,
    });
    if (handle) return handle;
    // The pivot crosshair, when centred, is grabbable too (to drag it out).
    if (nearWorld(pivot.x, pivot.y)) return "pivot";
    return null;
  }

  /** CSS cursor for a hovered handle. */
  cursorFor(handle: "knob" | "pivot" | ResizeHandle): string {
    if (handle === "knob") return "grab";
    if (handle === "pivot") return "move";
    return resizeCursorFor(handle);
  }

  private angleTo(
    pivot: { x: number; y: number },
    p: { x: number; y: number },
  ): number {
    return (Math.atan2(p.y - pivot.y, p.x - pivot.x) * 180) / Math.PI;
  }

  /** Try to start a drag on a press. Returns true when it claimed the gesture. */
  maybeStartDrag(
    cursor: { x: number; y: number },
    pointer?: PointerKind,
  ): boolean {
    const target = this.host.getTransformTarget();
    if (!target) return false;
    const box = target.bounds();
    if (!box) return false;
    const p = this.toModel(cursor);
    const handle = this.handleAtModel(p, box, pointer);
    if (!handle) return false;

    if (handle === "pivot") {
      this.pivotDrag = {
        key: target.key,
        moved: false,
        hadPivot: target.pivot() !== null,
      };
      return true;
    }
    if (handle === "knob") {
      // The rotation orbits the object's pivot in WORLD space (rotation is
      // absolute), so the start angle uses the raw cursor.
      const pivot = this.effectivePivot(box, this.targetRotation());
      this.rotateDrag = {
        key: target.key,
        pivot,
        startAngle: this.angleTo(pivot, p),
        lastDeg: 0,
        moved: false,
      };
      return true;
    }
    this.resizeDrag = {
      key: target.key,
      handle,
      orig: box,
      rot: this.targetRotation(),
      lastFlip: { x: false, y: false },
      lastNext: box,
      moved: false,
    };
    return true;
  }

  handleMove(s: PointerSample, cursor: { x: number; y: number }): boolean {
    const p = this.toModel(cursor);
    if (this.pivotDrag) return this.movePivot(p);
    if (this.rotateDrag) return this.moveRotate(p);
    if (this.resizeDrag) {
      // The dragged handle lands exactly on any enabled snap — a corner of
      // another shape, a point along an edge, a crossing, the grid (#1174).
      // Snapped where things are DRAWN, then mapped into model space; an edge
      // handle keeps only its own axis, so the edge lines up with the snap.
      const snapped = this.host.snapResizeHandle(cursor, this.resizeDrag.key);
      return this.moveResize(s, this.toModel(snapped));
    }
    return false;
  }

  private movePivot(p: { x: number; y: number }): boolean {
    const target = this.host.getTransformTarget();
    if (!target || target.key !== this.pivotDrag!.key) {
      this.reset();
      return true;
    }
    this.pivotDrag!.moved = true;
    this.host.getScene().setTransformPivotOverride(target.key, p.x, p.y);
    // Only the crosshair moves — the geometry (and so the frame, derived from the
    // model) stays put. Park the crosshair under the cursor.
    this.host.getScene().setTransformPreview({ x: p.x, y: p.y });
    return true;
  }

  private moveRotate(p: { x: number; y: number }): boolean {
    const drag = this.rotateDrag!;
    const target = this.host.getTransformTarget();
    if (!target || target.key !== drag.key) {
      this.reset();
      return true;
    }
    const deg = this.angleTo(drag.pivot, p) - drag.startAngle;
    if (Math.abs(deg) > 1e-4) drag.moved = true;
    drag.lastDeg = deg;
    // Preview the WHOLE transformed part (outline + holes + stitch + cutouts +
    // decorations) from the same model fn the commit uses; the frame is derived
    // from that preview model so it hugs the spinning geometry. The crosshair
    // stays pinned to the fixed orbit point (`drag.pivot`) as it spins.
    const previewPiece = target.previewRotate(deg, drag.pivot.x, drag.pivot.y);
    if (previewPiece)
      this.host.getScene().setTransformDragPreview(drag.pivot, previewPiece);
    else this.host.getScene().setTransformPreview(drag.pivot);
    // The same sweep-arc guide construction's rotation draws (#921): a ray from
    // the pivot to the grabbed direction, a ghost ray back to where the drag
    // started, and the arc between. The Scene gates it on the rays toggle.
    // Drawn, so in displayed space: shift the pivot and the cursor back out
    // (the angles don't change under a translation).
    const off = target.displayOffset?.() ?? { x: 0, y: 0 };
    this.host
      .getScene()
      .setGuideRays(
        rotationSweepRays(
          { x: drag.pivot.x + off.x, y: drag.pivot.y + off.y },
          (drag.startAngle * Math.PI) / 180,
          deg,
          { x: p.x + off.x, y: p.y + off.y },
          this.zoom(),
        ),
      );
    return true;
  }

  private moveResize(s: PointerSample, p: { x: number; y: number }): boolean {
    const drag = this.resizeDrag!;
    const target = this.host.getTransformTarget();
    if (!target || target.key !== drag.key) {
      this.reset();
      return true;
    }
    const settings = this.host.getSettings();
    const aspectLock =
      target.aspectLockOnly || isSizesSnapActive(settings, s.shiftKey);
    const fromCenter = isCenterSnapActive(settings, s.altKey);
    // Resize math runs in the object's UN-ROTATED frame: map the cursor there
    // first, so dragging a corner of a tilted object scales along its own axes
    // (no skew) rather than the screen axes (#971).
    const lp = this.toLocal(p, drag.rot);
    const { box: next, flip } = applyResizeStep(drag.orig, drag.handle, lp, {
      aspectLock,
      fromCenter,
      minSize: target.minSize,
    });
    if (
      Math.abs(next.width - drag.orig.width) > DRAG_SLOP_MM ||
      Math.abs(next.height - drag.orig.height) > DRAG_SLOP_MM
    ) {
      drag.moved = true;
    }
    drag.lastNext = next;
    drag.lastFlip = flip;
    // Preview the WHOLE transformed part from the same model fn the commit uses,
    // so holes / stitch / cutouts re-lay live (a longer seam grows more stitches
    // at the same pitch), not just the outline. The frame is derived from that
    // preview model; the crosshair sits at the resized object's effective pivot
    // (a parked override survives the resize).
    const previewPiece = target.previewResize(drag.orig, next, flip);
    const crosshair = resizeDragPivot(
      drag.orig,
      next,
      flip,
      drag.rot,
      this.pivotOverride(),
    );
    if (previewPiece)
      this.host.getScene().setTransformDragPreview(crosshair, previewPiece);
    else this.host.getScene().setTransformPreview(crosshair);
    return true;
  }

  handleUp(p: { x: number; y: number }): boolean {
    if (this.pivotDrag) {
      const drag = this.pivotDrag;
      this.pivotDrag = null;
      const target = this.host.getTransformTarget();
      const box = target?.bounds();
      const dragged = target
        ? this.host.getScene().getTransformPivotOverride(target.key)
        : null;
      // The live override was only ever a rehearsal; the parked pivot lives on
      // the model, so the drag COMMITS here. Dropping it back on the object's
      // default position clears it instead (#420 parity) — that's how you put
      // the pivot back without a separate control.
      this.host.getScene().setTransformPivotOverride(null);
      if (target && box && dragged && drag.moved && target.key === drag.key) {
        const rest = displayedBoxCenter(box, this.targetRotation());
        const atRest =
          Math.hypot(dragged.x - rest.x, dragged.y - rest.y) <
          snapRadiusFor(PIVOT_SNAP_BACK_MM, currentPointerKind());
        // A nudge that lands back on the default, on an object that had no
        // parked pivot to begin with, changed nothing — don't put an undo step
        // on the stack for it.
        if (!atRest || drag.hadPivot) {
          target.commitPivot(
            atRest ? `Reset ${target.noun} pivot` : `Move ${target.noun} pivot`,
            atRest ? null : dragged,
          );
        }
      }
      this.host.getScene().setTransformPreview(null);
      this.rerenderFromModel();
      this.host.refreshEditCursor(p);
      return true;
    }
    if (this.rotateDrag) {
      const drag = this.rotateDrag;
      this.rotateDrag = null;
      this.host.getScene().setTransformPreview(null);
      // The sweep guide belongs to the drag, not the object — release tears it
      // down, matching construction.
      this.host.getScene().setGuideRays(null);
      const target = this.host.getTransformTarget();
      if (target && target.key === drag.key && drag.moved) {
        target.commitRotate(
          `Rotate ${target.noun}`,
          drag.lastDeg,
          drag.pivot.x,
          drag.pivot.y,
        );
      } else {
        this.rerenderFromModel();
      }
      this.host.refreshEditCursor(p);
      return true;
    }
    if (this.resizeDrag) {
      const drag = this.resizeDrag;
      this.resizeDrag = null;
      this.host.clearResizeHandleSnap();
      this.host.getScene().setTransformPreview(null);
      const target = this.host.getTransformTarget();
      if (target && target.key === drag.key && drag.moved) {
        target.commitResize(
          `Resize ${target.noun}`,
          drag.orig,
          drag.lastNext,
          drag.lastFlip,
        );
      } else {
        this.rerenderFromModel();
      }
      this.host.refreshEditCursor(p);
      return true;
    }
    return false;
  }

  handleCancel(): boolean {
    if (!this.hasActiveDrag()) return false;
    this.reset();
    this.rerenderFromModel();
    return true;
  }

  /** Restore the live outline to model truth after a no-op / cancelled drag —
   *  the preview mutated the paper item in place, so a re-render resets it. */
  private rerenderFromModel(): void {
    this.host.getScene().rerender();
  }
}
